import { useEffect, useRef } from "react";
import Typed from "typed.js";

interface TypewriterHTMLProps {
  html: string;
  speed?: number;
  className?: string;
  onComplete?: () => void;
  onType?: () => void;
}

export function TypewriterHTML({
  html,
  speed = 5,
  className,
  onComplete,
  onType,
}: TypewriterHTMLProps) {
  const el = useRef<HTMLSpanElement>(null);
  const typed = useRef<Typed | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onTypeRef = useRef(onType);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onTypeRef.current = onType;
  }, [onComplete, onType]);

  useEffect(() => {
    if (!el.current) return;

    typed.current = new Typed(el.current, {
      strings: [html],
      typeSpeed: speed,
      showCursor: false,
      contentType: "html",
      loop: false,
      onStringTyped: () => {
        onCompleteRef.current?.();
      },
    });

    // Scroll during typing by observing content changes
    const observer = new MutationObserver(() => {
      onTypeRef.current?.();
    });

    observer.observe(el.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      typed.current?.destroy();
      observer.disconnect();
    };
  }, [html, speed]);

  return <span ref={el} className={className} />;
}
